// Dreamlink · Studio · theme tokens — vision-deck brand
//
// Maroon + cream editorial: Cormorant Garamond (display, italic emphasis ok)
// + DM Sans (body). Migrated from the Orbis Brand Kit (Geist + signal green)
// on 2026-06-07 as part of studio.imorbis.com -> studio.dreamlink.global.
//
// Source of truth: products/dreamlink/proposals/dreamlink-global-vision-deck.html
//
// All three exports (T, tokens, night) keep their shapes so existing components
// inheriting from them continue to compile and render coherently. Values map to
// the Dreamlink palette; the `night` export is a dark-maroon register for the
// public money/creator landing surfaces.

const MAROON = '#590b11';
const MAROON_DEEP = '#3d0810';
const CREAM = '#F5F2EE';
const INK = '#262626';

const SERIF = "'Cormorant Garamond', Georgia, 'Times New Roman', serif";
const SANS = "'DM Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";
const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

// ── T (operator dashboard tokens) ───────────────────────────────────────────
// Reads through to the CSS variables defined in global.css so a future theme
// picker can swap presets without touching every component.
export const T = {
  bg: 'var(--bg)',
  bgSoft: 'var(--bg-soft)',
  bgCard: 'var(--bg-card)',
  ink: 'var(--ink)',
  inkSoft: 'var(--ink-soft)',
  inkMute: 'var(--ink-mute)',
  rule: 'var(--rule)',
  accent: 'var(--accent)',
  accentSoft: 'var(--accent-soft)',
  live: 'var(--live)',
  warning: 'var(--warning)',
  warningSoft: 'var(--warning-soft)',
  error: 'var(--error)',
  errorSoft: 'var(--error-soft)',

  font: {
    // Cormorant for display/italic editorial headers, DM Sans for body.
    display: SERIF,
    italic: SERIF,
    body: SANS,
    mono: MONO,
  },

  radius: '6px',
  ease: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

export type Theme = typeof T;

// ── tokens (brand portal / public landing) ──────────────────────────────────
// Shape preserved so call sites compile. `night` scale maps to maroon so the
// Button `primary` variant (tokens.color.night[700]) renders as a maroon CTA.
export const tokens = {
  color: {
    // warm ink scale (ink -> brown -> warm grays)
    ink: { 900: INK, 700: '#4D3C3A', 500: '#6F5C53', 300: '#B6A59B', 100: '#D8CEC4' },
    paper: { '00': '#FFFFFF', 50: CREAM, 100: '#ECE7DF', 200: '#E2DBD1' },
    line: { 100: 'rgba(38, 38, 38, 0.12)' },
    // maroon scale — drives primary buttons + dark surfaces
    night: { 900: MAROON_DEEP, 700: MAROON, 500: '#73161d' },
    // accent palette: `sage` slot now carries the maroon brand accent.
    accent: { sage: MAROON, tan: '#6F5C53', rust: '#B3261E' },
  },
  font: {
    display: SERIF,
    body: SANS,
    mono: MONO,
  },
  space: {
    xs: 4, sm: 8, md: 12, base: 16, lg: 24, xl: 32, '2xl': 48, '3xl': 64, '4xl': 96, section: 128,
  },
  motion: {
    instant: '80ms', quick: '160ms', base: '240ms', slow: '400ms', editorial: '800ms',
    out: 'cubic-bezier(0.16, 1, 0.3, 1)',
  },
  radius: { sm: 4, base: 8, lg: 12, pill: 999 },
  shadow: {
    hover: '0 12px 32px rgba(38, 38, 38, 0.08)',
    focus: `0 0 0 2px ${CREAM}, 0 0 0 4px ${MAROON}`,
  },
} as const;

export type Tokens = typeof tokens;

// ── night (public money/creator landing) ────────────────────────────────────
// Dark-maroon register for the one intentionally-dark surface set. Cream text
// on deep maroon, cream CTA chips.
export const night = {
  color: {
    bg: '#1a0608',
    bgAlt: '#210709',
    surface: '#2a0a0c',
    surfaceHi: '#3a1013',
    line: '#4a1a1d',
    lineHi: '#5a2226',
    ink: CREAM,
    inkSoft: '#D8C9C2',
    inkMute: '#B09A92',
    accent: CREAM,
    accentInk: MAROON,
    paper: CREAM,
    paperInk: INK,
  },
  font: {
    sans: SANS,
    mono: MONO,
  },
  radius: { sm: 6, base: 8, lg: 12, pill: 999 },
  motion: {
    quick: '160ms',
    base: '240ms',
    out: 'cubic-bezier(0.16, 1, 0.3, 1)',
  },
} as const;

export type Night = typeof night;
